/*!
=========================================================
* Regiform IMS Frontend - DataTable Component
=========================================================
* Reusable data table component with filtering capabilities
* Supports both standard and modal modes with row selection
* Built with Ant Design Table and custom Filters component
=========================================================
*/
import { useMemo, useState, useCallback } from "react";
import { Table, Card } from "antd";
import PropTypes from "prop-types";
import { Button } from "components/common";
import { useBottomNotificationStore } from "../../../stores/bottomNotificationStore";
import "./DataTable.css";

function DataTable({
  filters = [],
  onRefresh,
  onClearFilters,
  columns = [],
  rows: _rows = [],
  isLoading = false,
  title,
  extra,
  className = "",
  tableProps = {},
  isModal = false,
  onRowsSelected,
  pagination = null, // Custom pagination config for backend pagination
  onTableChange = null, // Handler for table changes (pagination, sorting, filtering)
  addedList = [],
  addedListRef = "id",
  multiSelect = false,
  ...otherProps
}) {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  const rows = _rows.map((row) => {
    return {
      ...row,
      isDisabled: addedList.includes(row[addedListRef]),
    };
  });

  // Modal-specific state

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Handle row selection (for multi-select mode) - memoized to prevent re-renders
  const handleRowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (selectedRowKeys, selectedRows) => {
        setSelectedRowKeys(selectedRowKeys);
        setSelectedRows(selectedRows);
      },
      getCheckboxProps: (record) => ({
        name: record.code || record.id,
      }),
    }),
    [selectedRowKeys]
  );

  // Handle single row click (for single-select mode) - memoized to prevent re-renders
  const handleRowClick = useCallback(
    (record) => {
      if (isModal && !multiSelect && onRowsSelected) {
        onRowsSelected([record]);
      }
    },
    [isModal, multiSelect, onRowsSelected]
  );

  // Handle add selected items button click - memoized to prevent re-renders
  const handleAddSelectedItems = useCallback(() => {
    if (selectedRows.length === 0) {
      showNotification("Please select at least one item", "warning");
      return;
    }

    if (onRowsSelected) {
      onRowsSelected(selectedRows);
    }
  }, [selectedRows, onRowsSelected, showNotification]);

  // Configure table props based on mode - memoized to prevent re-renders
  const getTableProps = useMemo(() => {
    const baseProps = {
      columns,
      dataSource: rows,
      loading: isLoading,
      rowKey: (record) => record.id || record.key,
      pagination: pagination || {
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        pageSizeOptions: ["10", "20", "50", "100"],
        defaultPageSize: 10,
      },
      scroll: { x: "max-content" },
      className: "data-table",
      onChange: onTableChange,
      rowClassName: (record) => {
        if (record?.isDisabled) {
          return "already-added-row";
        }
        return "";
      },
      ...tableProps,
    };

    // Add modal-specific props
    if (isModal) {
      if (multiSelect) {
        baseProps.rowSelection = handleRowSelection;
      } else {
        baseProps.onRow = (record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        });
      }
    }

    return baseProps;
  }, [
    columns,
    rows,
    isLoading,
    pagination,
    onTableChange,
    tableProps,
    isModal,
    multiSelect,
    handleRowSelection,
    handleRowClick,
  ]);

  const Wrapper = useCallback(
    ({ children }) => {
      if (isModal) {
        return children;
      }

      return (
        <Card className={`data-table-card ${className}`} {...otherProps}>
          {children}
        </Card>
      );
    },
    [isModal, className, otherProps]
  );

  return (
    <Wrapper>
      {isModal && multiSelect && selectedRows.length > 0 && (
        <div className="data-table-modal-actions">
          <Button
            type="primary"
            onClick={handleAddSelectedItems}
            disabled={selectedRows.length === 0}
          >
            Add Selected Items ({selectedRows.length})
          </Button>
        </div>
      )}

      {/* Table Section */}
      <div className="data-table-section">
        <Table {...getTableProps} />
      </div>
    </Wrapper>
  );
}

DataTable.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["INPUT", "DROPDOWN"]).isRequired,
      onChange: PropTypes.func.isRequired,
      placeholder: PropTypes.string,
      value: PropTypes.any,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })
      ),
      prefix: PropTypes.node,
      allowClear: PropTypes.bool,
      style: PropTypes.object,
      className: PropTypes.string,
    })
  ),
  onRefresh: PropTypes.func,
  onClearFilters: PropTypes.func,
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  title: PropTypes.node,
  extra: PropTypes.node,
  className: PropTypes.string,
  tableProps: PropTypes.object,
  isModal: PropTypes.bool,
  onRowsSelected: PropTypes.func,
  pagination: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  onTableChange: PropTypes.func,
};

export default DataTable;
